import { Footnote, FootnotesState } from './FootnotesReducerTypes';
import { connectionName, FootnotesConnection, FootnotesDispatchers } from './FootnotesReducer';
import { initializeStore, SeamlessCoreStore } from '@seamless/store';
import Logger from '@owc-utils/common/Logger';

/**
 * add a connection to the seamless store
 *
 * @function addFootnotesConnection
 * @returns seamlessStore
 */
const addFootnotesConnection = async (): Promise<void> => {
  const store = initializeStore();
  return store.addConnection(new FootnotesConnection()).catch(() => Logger.debug('connection already added'));
};

/**
 * the connection from the FootnotesConnection
 *
 * @function getFootnotesConnection
 * @return Public dispatchers
 */
const getFootnotesConnection = async (): Promise<FootnotesDispatchers> => {
  const store = initializeStore();
  await addFootnotesConnection();
  return store.getConnectionDispatchers(connectionName);
};

let store: SeamlessCoreStore;

/**
 * Adds a list of footnotes to the store
 * ```js
 * import { addFootnotesToStore } from '@owc-utils/Footnotes/FootnotesHelper';
 * addFootnotesToStore(this.footnotesData.footnotes);
 * ```
 * @exports
 * @function addFootnotesToStore
 * @param footnotes
 * @returns Promise
 */
export const addFootnotesToStore = async (footnotes: Footnote[]) => {
  const footnotesConnection = await getFootnotesConnection();
  footnotesConnection.addFootnotes(footnotes);
};

/**
 * Mounts the footnotes elements inside the component (with <sup> element)
 * ```js
 * import { mountFootnotes } from '@owc-utils/Footnotes/FootnotesHelper';
 * mountFootnotes(domElement);
 * ```
 * eg.
 * <p>Your text goes here with <span data-footnote-id="abc123xyz">footnote</span></p>
 * turns into
 * <p>Your text goes here with <span data-footnote-id="abc123xyz">footnote<sup>1</sup></span></p>
 * @function mountFootnotes
 * @exports
 * @param componentElement
 */
export const mountFootnotes = (componentElement: Element) => {
  //TODO: ??=
  if (!store) {
    store = initializeStore();
  }

  const footnoteElements: HTMLElement[] = Array.from(componentElement.querySelectorAll('[data-footnote-id]'));

  // Don't subscribe if there's no footnote elements
  if (footnoteElements.length === 0) return;

  store.subscribe(connectionName, ({ footnotes: allFootnotes }: FootnotesState) => {
    footnoteElements.forEach(footnote => {
      // We know that this will always return a string because we can only reach this part of the method if
      // footnote.dataset.footnoteId exists. This is inside of an iteration that only runs in elements that
      // were selected for having this data attribute
      const currentFootnoteId = footnote.dataset.footnoteId as string,
        footnoteSup: HTMLElement | null = footnote.querySelector('sup'); // Used to check if the footnote already has a <sup> element

      // If footnote sup already exists, check if the number of the footnote changed
      if (footnoteSup && allFootnotes[currentFootnoteId]) {
        footnoteSup.textContent = `[${allFootnotes[currentFootnoteId].number}]`;
      }
      // If footnote matches the id on the markup, adds the `<sup>` with the correct footnote number
      else if (allFootnotes[currentFootnoteId] && !footnoteSup) {
        const numberElement: HTMLElement = document.createElement('sup');
        numberElement.textContent = `[${allFootnotes[currentFootnoteId].number}]`;
        footnote.appendChild(numberElement);
      }
    });
  });
};
